import { checklistAnswerMatchesRow } from '@/services/checklist/checklistObservations';
import { sha512Hex } from '@/services/sync/PendingOperationTerminalization';

export interface ArchivedChecklistPartial {
  id: string;
  empresa_id: string;
  user_id: string | null;
  operation: string;
  collection: string;
  item_id: string;
  data: string | null;
  original_timestamp: number;
  accounting_state: string;
  reason: string | null;
  payload_sha512: string;
}

export interface ArchivedPartialConflict {
  id: string;
  empresa_id: string;
  pending_operation_id: string | null;
  collection: string | null;
  item_id: string | null;
  field_id?: string | null;
  payload: string | null;
  payload_sha512: string | null;
  reason: string | null;
  status: string | null;
}

export interface ArchivedPartialValue {
  id: string;
  empresa_id: string;
  checklist_id: string;
  campo_id: string | null;
  snapshot_field_id: string | null;
  valor: string | null;
  observacao?: string | null;
}

export interface ArchivedPartialReplayPlan {
  status: 'invalid' | 'waiting' | 'ready' | 'complete';
  reason: string;
  replayId: string | null;
  pending: {
    id: string;
    operation: 'checklist_partial_update';
    collection: 'guincho_valores_checklist';
    item_id: string;
    data: string;
    timestamp: number;
    user_id: string | null;
    empresa_id: string;
  } | null;
  unresolvedFieldIds: string[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA512 = /^[0-9a-f]{128}$/;

const replayIdFor = (sourceId: string, digest: string): string => {
  return `archived-replay:${sha512Hex(
    `archived-partial-replay:v1:${sourceId}:${digest}`
  ).slice(0, 40)}`;
};

const invalid = (reason: string): ArchivedPartialReplayPlan => ({
  status: 'invalid',
  reason,
  replayId: null,
  pending: null,
  unresolvedFieldIds: [],
});

export const planArchivedChecklistPartialReplay = (input: {
  companyId: string;
  archive: ArchivedChecklistPartial;
  conflict: ArchivedPartialConflict | null;
  localValues: ReadonlyArray<ArchivedPartialValue>;
  remoteValues: ReadonlyArray<ArchivedPartialValue>;
  terminalFieldIds: ReadonlySet<string>;
  snapshotOriginalById: ReadonlyMap<string, string>;
}): ArchivedPartialReplayPlan => {
  const { companyId, archive, conflict, localValues, remoteValues } = input;
  if (!UUID.test(companyId) || archive.empresa_id !== companyId ||
    !UUID.test(archive.item_id) || !Number.isSafeInteger(archive.original_timestamp) ||
    archive.original_timestamp <= 0 || archive.operation !== 'checklist_partial_update' ||
    archive.collection !== 'guincho_valores_checklist' ||
    archive.accounting_state !== 'conflict_materialized' ||
    archive.reason !== 'tombstoned_entity' || !archive.data ||
    archive.data.length > 2097152 || !SHA512.test(archive.payload_sha512) ||
    sha512Hex(archive.data) !== archive.payload_sha512) {
    return invalid('archive_mismatch');
  }
  const operationId = archive.id.match(/^accounted:tombstone:([0-9a-f-]{36}):([0-9a-f]{24})$/i)?.[1];
  if (!operationId || !UUID.test(operationId) ||
    archive.id !== `accounted:tombstone:${operationId}:${archive.payload_sha512.slice(0, 24)}` ||
    !conflict || conflict.id !== `conflict:tombstone:${operationId}:${archive.payload_sha512.slice(0, 24)}` ||
    conflict.empresa_id !== companyId || conflict.pending_operation_id !== operationId ||
    conflict.collection !== archive.collection || conflict.item_id !== archive.item_id ||
    conflict.payload !== archive.data || conflict.payload_sha512 !== archive.payload_sha512 ||
    conflict.reason !== 'tombstoned_entity' || !['open', 'blocked'].includes(conflict.status ?? '')) {
    return invalid('conflict_mismatch');
  }

  let answers: Record<string, unknown>;
  try {
    answers = JSON.parse(archive.data);
  } catch {
    return invalid('payload_invalid');
  }
  if (!answers || typeof answers !== 'object' || Array.isArray(answers) ||
    Object.keys(answers).length === 0 || Object.keys(answers).length > 500 ||
    Object.keys(answers).some(id => !UUID.test(id))) {
    return invalid('payload_invalid');
  }

  const replayId = replayIdFor(operationId, archive.payload_sha512);
  const pendingAnswers: Record<string, unknown> = {};
  const unresolvedFieldIds: string[] = [];
  const rowsFor = (rows: ReadonlyArray<ArchivedPartialValue>, fieldId: string) => rows.filter(row =>
    row.empresa_id === companyId && row.checklist_id === archive.item_id &&
    (row.campo_id === fieldId ||
      (row.snapshot_field_id && input.snapshotOriginalById.get(row.snapshot_field_id) === fieldId))
  );

  for (const [fieldId, answer] of Object.entries(answers)) {
    const local = rowsFor(localValues, fieldId);
    const remote = rowsFor(remoteValues, fieldId);
    if (local.length !== 1 || !checklistAnswerMatchesRow(answer, local[0]) || remote.length > 1 ||
      remoteValues.some(row => row.id === local[0].id && !remote.includes(row))) {
      unresolvedFieldIds.push(fieldId);
      continue;
    }
    if (remote.length === 1) {
      if (remote[0].id !== local[0].id || !checklistAnswerMatchesRow(answer, remote[0])) {
        unresolvedFieldIds.push(fieldId);
      }
      continue;
    }
    if (input.terminalFieldIds.has(fieldId)) {
      unresolvedFieldIds.push(fieldId);
      continue;
    }
    pendingAnswers[fieldId] = answer;
    unresolvedFieldIds.push(fieldId);
  }

  if (unresolvedFieldIds.length === 0) {
    return { status: 'complete', reason: 'remote_equivalent', replayId, pending: null, unresolvedFieldIds };
  }
  if (Object.keys(pendingAnswers).length === 0) {
    return { status: 'waiting', reason: 'no_safe_fields', replayId, pending: null, unresolvedFieldIds };
  }
  return {
    status: 'ready',
    reason: 'verified_missing_fields',
    replayId,
    pending: {
      id: replayId,
      operation: 'checklist_partial_update',
      collection: 'guincho_valores_checklist',
      item_id: archive.item_id,
      data: JSON.stringify(pendingAnswers),
      timestamp: archive.original_timestamp,
      user_id: archive.user_id,
      empresa_id: archive.empresa_id,
    },
    unresolvedFieldIds,
  };
};

/**
 * Uma falha V2 por campo já saiu da fila ativa. A nova tentativa só nasce
 * quando a referência voltou a ser resolúvel e o valor capturado segue igual.
 */
export const planArchivedChecklistFieldReplay = (input: {
  companyId: string;
  archive: ArchivedChecklistPartial;
  conflict: ArchivedPartialConflict | null;
  localValues: ReadonlyArray<ArchivedPartialValue>;
  remoteValues: ReadonlyArray<ArchivedPartialValue>;
  resolvableFieldIds: ReadonlySet<string>;
}): ArchivedPartialReplayPlan => {
  const { companyId, archive, conflict } = input;
  if (!UUID.test(companyId) || archive.empresa_id !== companyId ||
    !UUID.test(archive.item_id) || !Number.isSafeInteger(archive.original_timestamp) ||
    archive.original_timestamp <= 0 || archive.operation !== 'checklist_partial_update' ||
    archive.collection !== 'guincho_valores_checklist' ||
    archive.accounting_state !== 'conflict_materialized' ||
    archive.reason !== 'field_reference_not_resolved' || !archive.data ||
    archive.data.length > 1048576 || !SHA512.test(archive.payload_sha512)) {
    return invalid('archive_mismatch');
  }
  const match = archive.id.match(/^accounted:([0-9a-f-]{36}):([0-9a-f-]{36}):([0-9a-f]{24})$/i);
  const operationId = match?.[1];
  const fieldId = match?.[2];
  if (!operationId || !fieldId || !UUID.test(operationId) || !UUID.test(fieldId) ||
    match?.[3] !== archive.payload_sha512.slice(0, 24) ||
    !conflict || conflict.id !== `conflict:${operationId}:${fieldId}:${match[3]}` ||
    conflict.empresa_id !== companyId || conflict.pending_operation_id !== operationId ||
    conflict.collection !== archive.collection || conflict.item_id !== archive.item_id ||
    conflict.field_id !== fieldId ||
    conflict.payload_sha512 !== archive.payload_sha512 ||
    conflict.reason !== archive.reason || !['open', 'blocked'].includes(conflict.status ?? '')) {
    return invalid('conflict_mismatch');
  }
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(archive.data);
  } catch {
    return invalid('payload_invalid');
  }
  const payload = data?.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload) ||
    data.field_id !== fieldId || !conflict.payload ||
    JSON.stringify(payload) !== conflict.payload ||
    data.idempotency_key !== (payload as Record<string, unknown>).idempotency_key ||
    !UUID.test(String((payload as Record<string, unknown>).id ?? '')) ||
    (payload as Record<string, unknown>).campo_id !== fieldId ||
    (payload as Record<string, unknown>).snapshot_field_id !== null ||
    (payload as Record<string, unknown>).expected_updated_at !== null ||
    typeof (payload as Record<string, unknown>).valor !== 'string' ||
    sha512Hex(conflict.payload) !== archive.payload_sha512) {
    return invalid('payload_invalid');
  }
  const valueId = (payload as Record<string, unknown>).id as string;
  const value = (payload as Record<string, unknown>).valor as string;
  const hasNote = Object.prototype.hasOwnProperty.call(payload, 'observacao');
  const note = (payload as Record<string, unknown>).observacao;
  if (hasNote && note !== null && typeof note !== 'string') return invalid('payload_invalid');
  const local = input.localValues.filter(row =>
    row.empresa_id === companyId && row.checklist_id === archive.item_id &&
    row.campo_id === fieldId
  );
  const remote = input.remoteValues.filter(row =>
    row.empresa_id === companyId && row.checklist_id === archive.item_id &&
    (row.campo_id === fieldId || row.id === valueId)
  );
  const replayId = replayIdFor(operationId + ':' + fieldId, archive.payload_sha512);
  if (local.length !== 1 || local[0].id !== valueId || local[0].valor !== value || (hasNote && (local[0].observacao ?? null) !== note) ||
    remote.length > 1 || !input.resolvableFieldIds.has(fieldId)) {
    return {
      status: 'waiting', reason: 'field_or_value_changed', replayId, pending: null,
      unresolvedFieldIds: [fieldId],
    };
  }
  if (remote.length === 1) {
    return remote[0].id === valueId && remote[0].campo_id === fieldId &&
      remote[0].valor === value && (!hasNote || (remote[0].observacao ?? null) === note)
      ? { status: 'complete', reason: 'remote_equivalent', replayId, pending: null, unresolvedFieldIds: [] }
      : { status: 'waiting', reason: 'remote_diverged', replayId, pending: null, unresolvedFieldIds: [fieldId] };
  }
  return {
    status: 'ready',
    reason: 'verified_missing_field',
    replayId,
    pending: {
      id: replayId,
      operation: 'checklist_partial_update',
      collection: 'guincho_valores_checklist',
      item_id: archive.item_id,
      data: JSON.stringify({ [fieldId]: hasNote ? { valor: value, observacao: note } : value }),
      timestamp: archive.original_timestamp,
      user_id: archive.user_id,
      empresa_id: archive.empresa_id,
    },
    unresolvedFieldIds: [fieldId],
  };
};
